// src/components/recovery/RecoveryChat.tsx - IMPROVED VERSION (Hides loading, auto-scrolls)
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import {
  getRecoveryChats,
  sendRecoveryMessage,
  markAllAsRead,
  addMessage,
  getUnreadChatCount,
} from "../../redux/slices/recoveryChatSlice";
import { GlobalStyles } from "../../styles/globalStyles";
import toast from "react-hot-toast";
import {
  Send,
  Paperclip,
  X,
  Image as ImageIcon,
  File,
  DownloadCloud,
  Loader2,
  User,
  Shield,
  Check,
  AlertCircle,
  MessageSquare,
  Eye,
} from "lucide-react";

interface RecoveryChatProps {
  recoveryId: number;
  caseNumber: string;
  isAdmin: boolean;
  onClose: () => void;
  isOpen: boolean;
}

interface ChatMessage {
  id: number;
  recovery_request_id: number;
  user_id: number | null;
  admin_id: number | null;
  message: string;
  is_admin: boolean;
  attachments: any[];
  read_at: string | null;
  created_at: string;
  user?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  admin?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  tempId?: number;
}

// Helper to normalize API data
const normalizeChatMessage = (chat: any): ChatMessage => ({
  ...chat,
  id: Number(chat.id),
  recovery_request_id: Number(chat.recovery_request_id),
  user_id: chat.user_id ? Number(chat.user_id) : null,
  admin_id: chat.admin_id ? Number(chat.admin_id) : null,
  is_admin: chat.is_admin === "1" || chat.is_admin === true,
  attachments: Array.isArray(chat.attachments) ? chat.attachments : [],
  read_at: chat.read_at,
  created_at: chat.created_at,
  user: chat.user,
  admin: chat.admin,
});

const RecoveryChat: React.FC<RecoveryChatProps> = ({
  recoveryId,
  caseNumber,
  isAdmin: currentUserIsAdmin,
  onClose,
  isOpen,
}) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { chats, isLoading } = useSelector(
    (state: RootState) => state.recoveryChats
  );

  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [localConnectionStatus, setLocalConnectionStatus] = useState<
    "checking" | "ready" | "error"
  >("checking");
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initializedRef = useRef<boolean>(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Normalize all chats
  const normalizedChats = React.useMemo(() => {
    return chats.map(normalizeChatMessage);
  }, [chats]);

  // Sort and deduplicate
  const uniqueChats = React.useMemo(() => {
    const seen = new Set<string>();
    const result: ChatMessage[] = [];

    // Sort by created_at (oldest to newest)
    const sortedChats = [...normalizedChats].sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    for (const chat of sortedChats) {
      const key = chat.id ? `id_${chat.id}` : `temp_${chat.tempId}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push(chat);
      }
    }

    return result;
  }, [normalizedChats]);

  // Listen for incoming Pusher messages
  useEffect(() => {
    const handlePusherMessage = (event: CustomEvent) => {
      const { message: chatMessage, caseId } = event.detail;

      if (caseId === recoveryId && isOpen) {
        // Normalize the incoming message
        const normalizedMessage = normalizeChatMessage(chatMessage);
        dispatch(addMessage(normalizedMessage));

        // Scroll to bottom after a short delay
        setTimeout(() => {
          if (messagesEndRef.current && isLoadingComplete) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 50);
      }
    };

    window.addEventListener(
      "pusher-message-received" as any,
      handlePusherMessage
    );
    return () => {
      window.removeEventListener(
        "pusher-message-received" as any,
        handlePusherMessage
      );
    };
  }, [dispatch, recoveryId, isOpen, isLoadingComplete]);

  // Load ALL messages when chat opens
  useEffect(() => {
    if (isOpen && recoveryId && !initializedRef.current) {
      console.log("📥 Loading ALL chat messages for case", recoveryId);
      initializedRef.current = true;
      setLocalConnectionStatus("checking");
      setIsLoadingComplete(false);

      const loadAllChats = async () => {
        try {
          // FIRST: Hide the messages container and show loading
          if (messagesContainerRef.current) {
            messagesContainerRef.current.style.opacity = "0";
            messagesContainerRef.current.style.visibility = "hidden";
          }

          // Load ALL chats (not just page 1)
          await dispatch(
            getRecoveryChats({
              recoveryId,
              loadAll: true,
            }) as any
          );

          // Mark all as read
          await dispatch(markAllAsRead(recoveryId) as any);

          // Set status to ready
          setLocalConnectionStatus("ready");
          setIsLoadingComplete(true);

          // Wait a tiny bit for DOM to update, then show and scroll
          setTimeout(() => {
            if (messagesContainerRef.current) {
              // Show the messages container
              messagesContainerRef.current.style.opacity = "1";
              messagesContainerRef.current.style.visibility = "visible";

              // Scroll to bottom
              if (messagesEndRef.current) {
                messagesEndRef.current.scrollIntoView({ behavior: "auto" });
              }
            }
          }, 100);
        } catch (error) {
          console.error("Failed to load chat:", error);
          toast.error("Failed to load chat messages");
          setLocalConnectionStatus("error");

          // Still show container even on error
          if (messagesContainerRef.current) {
            messagesContainerRef.current.style.opacity = "1";
            messagesContainerRef.current.style.visibility = "visible";
          }
          setIsLoadingComplete(true);
        }
      };

      loadAllChats();
    }

    // Reset on close
    if (!isOpen) {
      initializedRef.current = false;
      setLocalConnectionStatus("checking");
      setIsLoadingComplete(false);
    }
  }, [isOpen, recoveryId, dispatch]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (uniqueChats.length > 0 && isOpen && isLoadingComplete) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  }, [uniqueChats.length, isOpen, isLoadingComplete]);

  // SIMPLIFIED: Get message type
  const getMessageType = (chat: ChatMessage): "self" | "admin" | "user" => {
    const userId = user?.id;

    // Message from current user
    if (chat.is_admin && currentUserIsAdmin && chat.admin_id === userId) {
      return "self";
    }
    if (!chat.is_admin && !currentUserIsAdmin && chat.user_id === userId) {
      return "self";
    }

    // Message from admin (when current user is regular user)
    if (chat.is_admin && !currentUserIsAdmin) {
      return "admin";
    }

    // Message from user (when current user is admin)
    if (!chat.is_admin && currentUserIsAdmin) {
      return "user";
    }

    return chat.is_admin ? "admin" : "user";
  };

  // SIMPLIFIED: Get display name
  const getSenderName = (chat: ChatMessage): string => {
    const messageType = getMessageType(chat);

    if (messageType === "self") {
      return "You";
    }

    if (messageType === "admin") {
      // Admin message (not from current user)
      if (chat.admin?.first_name) {
        const name = `${chat.admin.first_name} ${chat.admin.last_name}`;
        return currentUserIsAdmin ? `${name} (Admin)` : name;
      }
      return currentUserIsAdmin ? "Other Admin" : "Support";
    }

    // User message (not from current user)
    if (chat.user?.first_name) {
      return `${chat.user.first_name} ${chat.user.last_name}`;
    }
    return "User";
  };

  // SIMPLIFIED: Get sender icon
  const getSenderIcon = (chat: ChatMessage) => {
    const messageType = getMessageType(chat);

    if (messageType === "self") {
      return <User className="h-3 w-3" />;
    }

    if (messageType === "admin") {
      return <Shield className="h-3 w-3 text-blue-500" />;
    }

    return <User className="h-3 w-3" />;
  };

  const handleSendMessage = async () => {
    if (!message.trim() && attachments.length === 0) {
      toast.error("Please enter a message or attach a file");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    setSending(true);
    try {
      // Create optimistic update
      const tempMessageId = Date.now();
      const optimisticMessage: ChatMessage = {
        id: tempMessageId,
        recovery_request_id: recoveryId,
        user_id: currentUserIsAdmin ? null : user.id,
        admin_id: currentUserIsAdmin ? user.id : null,
        message: message.trim(),
        is_admin: currentUserIsAdmin,
        attachments: attachments.map((file) => ({
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
        })),
        read_at: null,
        created_at: new Date().toISOString(),
        tempId: tempMessageId,
        ...(currentUserIsAdmin
          ? {
              admin: {
                id: user.id,
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                email: user.email || "",
              },
            }
          : {
              user: {
                id: user.id,
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                email: user.email || "",
              },
            }),
      };

      // Normalize and add
      const normalizedOptimistic = normalizeChatMessage(optimisticMessage);
      dispatch(addMessage(normalizedOptimistic));

      // Clear inputs
      const messageToSend = message.trim();
      const attachmentsToSend = [...attachments];
      setMessage("");
      setAttachments([]);

      // Scroll to bottom
      setTimeout(() => {
        if (messagesEndRef.current && isLoadingComplete) {
          messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
      }, 50);

      // Send to server
      await dispatch(
        sendRecoveryMessage({
          recoveryId,
          message: messageToSend,
          attachments: attachmentsToSend,
        }) as any
      );

      dispatch(getUnreadChatCount() as any);
    } catch (error) {
      console.error("❌ Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    const oversizedFiles = newFiles.filter(
      (file) => file.size > 5 * 1024 * 1024
    );
    if (oversizedFiles.length > 0) {
      toast.error("Some files exceed 5MB limit");
      return;
    }

    if (attachments.length + newFiles.length > 5) {
      toast.error("Maximum 5 attachments allowed");
      return;
    }

    setAttachments([...attachments, ...newFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Render message content
  const renderMessageContent = (chat: ChatMessage) => {
    const messageType = getMessageType(chat);
    const senderName = getSenderName(chat);
    const senderIcon = getSenderIcon(chat);
    const isCurrentUser = messageType === "self";

    return (
      <div
        className={`flex ${
          isCurrentUser ? "justify-end" : "justify-start"
        } mb-4`}
        key={chat.id || chat.tempId}
      >
        <div
          className={`max-w-[70%] rounded-lg p-3 ${
            isCurrentUser
              ? "bg-blue-500 text-white rounded-br-none"
              : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-none"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            {senderIcon}
            <span className="text-xs font-medium">{senderName}</span>
            <span className="text-xs opacity-75">
              {formatTime(chat.created_at)}
            </span>
            {chat.read_at && isCurrentUser && (
              <Check className="h-3 w-3 ml-1 text-green-500" />
            )}
          </div>

          {chat.message && (
            <p className="text-sm whitespace-pre-wrap mb-2">{chat.message}</p>
          )}

          {chat.attachments && chat.attachments.length > 0 && (
            <div className="space-y-2 mt-2">
              {chat.attachments.map((attachment: any, index: number) => (
                <div
                  key={index}
                  className={`flex items-center gap-2 p-2 rounded ${
                    isCurrentUser
                      ? "bg-blue-600/30"
                      : "bg-gray-200 dark:bg-gray-600"
                  }`}
                >
                  {attachment.type?.startsWith("image/") ? (
                    <ImageIcon className="h-4 w-4 flex-shrink-0" />
                  ) : (
                    <File className="h-4 w-4 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate">{attachment.name}</p>
                    <p className="text-xs opacity-75">
                      {(attachment.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-1 rounded ${
                      isCurrentUser
                        ? "hover:bg-blue-600/50"
                        : "hover:bg-gray-300 dark:hover:bg-gray-500"
                    }`}
                    title="Download"
                  >
                    <DownloadCloud className="h-3 w-3" />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Group messages by date
  const groupedMessages = uniqueChats.reduce(
    (groups: any, chat: ChatMessage) => {
      const date = formatDate(chat.created_at);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(chat);
      return groups;
    },
    {}
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-primary" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Recovery Chat: {caseNumber}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {currentUserIsAdmin
                    ? `Communicating with user (You are Admin)`
                    : `Communicating with support team (You are User)`}
                  {localConnectionStatus === "ready" && " • Live"}
                  {localConnectionStatus === "error" && " • Connection Error"}
                  {localConnectionStatus === "checking" && " • Loading..."}
                  {isLoadingComplete &&
                    uniqueChats.length > 0 &&
                    ` • ${uniqueChats.length} messages`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  dispatch(markAllAsRead(recoveryId) as any);
                  toast.success("All messages marked as read");
                }}
                className={`${GlobalStyles.button} ${GlobalStyles.buttonGhost} ${GlobalStyles.buttonSmall}`}
                title="Mark all as read"
              >
                <Eye className="h-4 w-4" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Messages Container with Loading Overlay */}
          <div className="flex-1 overflow-hidden relative">
            {/* Loading Overlay - Shows while loading */}
            {!isLoadingComplete && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white dark:bg-gray-800">
                <div className="text-center">
                  <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Loading chat messages...
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400">
                    Loading all messages, please wait
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                    {uniqueChats.length > 0
                      ? `${uniqueChats.length} loaded so far`
                      : "Starting..."}
                  </p>
                </div>
              </div>
            )}

            {/* Messages Container - Hidden until loading complete */}
            <div
              ref={messagesContainerRef}
              className={`h-full overflow-y-auto p-4 transition-opacity duration-300 ${
                isLoadingComplete ? "opacity-100" : "opacity-0 invisible"
              }`}
            >
              {uniqueChats.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="h-12 w-12 text-gray-400 mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No messages yet
                  </h4>
                  <p className="text-gray-500 dark:text-gray-400">
                    Start the conversation by sending a message
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedMessages).map(
                    ([date, dateMessages]: [string, any]) => (
                      <div key={date}>
                        <div className="flex items-center justify-center my-4">
                          <div className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                            <span className="text-xs text-gray-600 dark:text-gray-300">
                              {isToday(date) ? "Today" : date}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {dateMessages.map((chat: ChatMessage) =>
                            renderMessageContent(chat)
                          )}
                        </div>
                      </div>
                    )
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>
          </div>

          {/* Message Input - Disabled until loading complete */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-4">
            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Attachments ({attachments.length}/5)
                  </span>
                  <button
                    onClick={() => setAttachments([])}
                    className="text-xs text-red-500 hover:text-red-600"
                  >
                    Clear all
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                    >
                      {file.type.startsWith("image/") ? (
                        <ImageIcon className="h-4 w-4 text-blue-500" />
                      ) : (
                        <File className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="text-sm truncate max-w-[150px]">
                        {file.name}
                      </span>
                      <button
                        onClick={() => removeAttachment(index)}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3">
              {/* File Attachment Button */}
              <div className="relative">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`${
                    GlobalStyles.iconButton
                  } text-gray-700 dark:text-gray-300 ${
                    !isLoadingComplete ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  title="Attach files"
                  disabled={sending || !isLoadingComplete}
                >
                  <Paperclip className="h-5 w-5" />
                </button>
              </div>

              {/* Message Input */}
              <div className="flex-1 relative">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    isLoadingComplete
                      ? "Type your message here..."
                      : "Loading chat, please wait..."
                  }
                  className={`${
                    GlobalStyles.input
                  } w-full min-h-[44px] max-h-32 resize-none pr-12 ${
                    !isLoadingComplete
                      ? "opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700"
                      : ""
                  }`}
                  disabled={sending || !isLoadingComplete}
                  rows={1}
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <button
                    onClick={handleSendMessage}
                    disabled={
                      (!message.trim() && attachments.length === 0) ||
                      sending ||
                      !isLoadingComplete
                    }
                    className={`p-2 rounded-lg ${
                      (!message.trim() && attachments.length === 0) ||
                      sending ||
                      !isLoadingComplete
                        ? "bg-gray-300 dark:bg-gray-600 cursor-not-allowed"
                        : "bg-primary hover:bg-primary/90"
                    } text-white transition-colors`}
                    title={
                      !isLoadingComplete
                        ? "Please wait while chat loads..."
                        : "Send message"
                    }
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Help Text */}
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <AlertCircle className="h-3 w-3" />
              <span>
                {isLoadingComplete
                  ? "Press Enter to send • Shift+Enter for new line • Max 5 attachments (5MB each)"
                  : "Loading all messages, please wait..."}
              </span>
            </div>
          </div>

          {/* Connection Status */}
          <div className="px-4 pb-3">
            <div className="flex items-center gap-2 text-xs">
              <div
                className={`h-2 w-2 rounded-full ${
                  localConnectionStatus === "ready"
                    ? "bg-green-500 animate-pulse"
                    : localConnectionStatus === "checking"
                    ? "bg-blue-500 animate-pulse"
                    : "bg-red-500"
                }`}
              />
              <span className="text-gray-500 dark:text-gray-400">
                {isLoadingComplete
                  ? localConnectionStatus === "ready"
                    ? "Connected (real-time)"
                    : localConnectionStatus === "error"
                    ? "Connection error"
                    : "Loading..."
                  : "Loading all messages..."}
              </span>
              {sending && (
                <span className="flex items-center gap-1 text-blue-500">
                  <Loader2 className="h-2 w-2 animate-spin" />
                  Sending...
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecoveryChat;
